import { useState, useEffect, useRef } from 'react';
import * as Location from 'expo-location';
import { LocationRegion } from '../lib/types';
import { Platform } from 'react-native';
import { normalizeLocationError, reportLocationErrorIfActionable, LOCATION_ERROR } from '../utils/locationUtils';

export const useForegroundLocation = (enabled: boolean = true) => {
  
  const [location, setLocation] = useState<LocationRegion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const initializingRef = useRef(false);
  const subscriptionRef = useRef<Location.LocationSubscription | null>(null);
  const webIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Throttle/gate map-driving location updates to avoid re-render storms and MapView flicker
  const lastEmittedLocationRef = useRef<LocationRegion | null>(null);
  const lastEmitAtMsRef = useRef<number>(0);

  const clearLocationState = () => {
    setLocation(null);
    lastEmittedLocationRef.current = null;
    lastEmitAtMsRef.current = 0;
  };

  const distanceMeters = (a: { latitude: number; longitude: number }, b: { latitude: number; longitude: number }) => {
    const R = 6371000;
    const toRad = (d: number) => (d * Math.PI) / 180;
    const dLat = toRad(b.latitude - a.latitude);
    const dLon = toRad(b.longitude - a.longitude);
    const lat1 = toRad(a.latitude);
    const lat2 = toRad(b.latitude);
    const s = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * (Math.sin(dLon / 2) ** 2);
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
  };

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Prevent multiple simultaneous initializations
    if (initializingRef.current) {
      return;
    }

    let isMounted = true;
    initializingRef.current = true;

    const initLocation = async () => {
      try {
        // Skip permission check on web - web handles permissions differently
        if (Platform.OS !== 'web') {
          const { status } = await Location.getForegroundPermissionsAsync();
          
          if (status !== 'granted') {
            const { status: newStatus } = await Location.requestForegroundPermissionsAsync();
            
            if (newStatus !== 'granted') {
              const errorMsg = `Permission denied. Status: ${newStatus}. Please enable location access in your phone settings.`;
              console.error('[useForegroundLocation] ❌', errorMsg);
              clearLocationState();
              setError(LOCATION_ERROR.PERMISSION_DENIED);
              return;
            }
          }
        }
        
        if (Platform.OS === 'web') {
          
          // For web, use getCurrentPositionAsync periodically
          const updateLocation = async () => {
            if (!isMounted) return;
            try {
              const position = await Location.getCurrentPositionAsync({
                accuracy: Location.Accuracy.Balanced,
                timeInterval: 5000,
              });
              const updatedLocation = {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                latitudeDelta: 0.0922,
                longitudeDelta: 0.0421,
              };

              const now = Date.now();
              const last = lastEmittedLocationRef.current;
              const moved = last ? distanceMeters(last, updatedLocation) : Infinity;
              if (!last || moved >= 5 || now - lastEmitAtMsRef.current >= 1000) {
                lastEmittedLocationRef.current = updatedLocation;
                lastEmitAtMsRef.current = now;
                setLocation(updatedLocation);
              }
              setError(null);
            } catch (error: unknown) {
              const normalized = normalizeLocationError(error);
              console.error('[useForegroundLocation]', normalized);
              reportLocationErrorIfActionable(normalized, Platform.OS);
              if (isMounted) {
                if (normalized.message === LOCATION_ERROR.PERMISSION_DENIED) {
                  clearLocationState();
                  setError(LOCATION_ERROR.PERMISSION_DENIED);
                } else if (normalized.message === LOCATION_ERROR.SETTINGS_INVALID) {
                  setError(LOCATION_ERROR.SETTINGS_INVALID);
                } else {
                  setError(`Error getting location: ${normalized.message}`);
                }
              }
            }
          };

          // Initial update
          void updateLocation();

          // Update every 10 seconds
          webIntervalRef.current = setInterval(() => {
            void updateLocation();
          }, 10000);
        } else {
          
          // Get initial position first
          try {
            const initialPosition = await Location.getCurrentPositionAsync({
              accuracy: Location.Accuracy.Balanced,
            });
            if (isMounted) {
              const initialLocation = {
                latitude: initialPosition.coords.latitude,
                longitude: initialPosition.coords.longitude,
                latitudeDelta: 0.0922,
                longitudeDelta: 0.0421,
              };
              setLocation(initialLocation);
              setError(null);
            }
          } catch (err: unknown) {
            const normalized = normalizeLocationError(err);
            console.error('[useForegroundLocation] Error getting initial position:', normalized);
            reportLocationErrorIfActionable(normalized, Platform.OS);
            if (isMounted) {
              if (normalized.message === LOCATION_ERROR.PERMISSION_DENIED) {
                clearLocationState();
                setError(LOCATION_ERROR.PERMISSION_DENIED);
              } else if (normalized.message === LOCATION_ERROR.SETTINGS_INVALID) {
                setError(LOCATION_ERROR.SETTINGS_INVALID);
              } else {
                setError(`Error getting initial position: ${normalized.message}`);
              }
            }
          }

          // Then watch for updates
          subscriptionRef.current = await Location.watchPositionAsync(
            { 
              accuracy: Location.Accuracy.Balanced,
              timeInterval: 5000,
              distanceInterval: 10,
            },
            (newLocation) => {
              if (!isMounted) return;
              const updatedLocation = {
                latitude: newLocation.coords.latitude,
                longitude: newLocation.coords.longitude,
                latitudeDelta: 0.0922,
                longitudeDelta: 0.0421,
              };

              // Gate noisy updates (tiny deltas) to reduce MapView region churn.
              const now = Date.now();
              const last = lastEmittedLocationRef.current;
              const dt = now - lastEmitAtMsRef.current;
              const moved = last ? distanceMeters(last, updatedLocation) : Infinity;

              // Emit at most ~1/sec, and only if moved meaningfully (>= 5m) or first update.
              if (!last || moved >= 5 || dt >= 1000) {
                lastEmittedLocationRef.current = updatedLocation;
                lastEmitAtMsRef.current = now;
                setLocation(updatedLocation);
              }
              setError(null);
            }
          );
        }
      } catch (error: unknown) {
        const normalized = normalizeLocationError(error);
        console.error('[useForegroundLocation] ❌ Error setting up location tracking:', normalized);
        reportLocationErrorIfActionable(normalized, Platform.OS);
        if (normalized.message === LOCATION_ERROR.PERMISSION_DENIED) {
          clearLocationState();
          setError(LOCATION_ERROR.PERMISSION_DENIED);
        } else if (normalized.message === LOCATION_ERROR.SETTINGS_INVALID) {
          setError(LOCATION_ERROR.SETTINGS_INVALID);
        } else {
          setError(`Setup error: ${normalized.message}`);
        }
      }
    };
    
    initLocation().finally(() => {
      initializingRef.current = false;
    });

    return () => {
      isMounted = false;
      if (subscriptionRef.current?.remove) {
        subscriptionRef.current.remove();
        subscriptionRef.current = null;
      }
      if (webIntervalRef.current) {
        clearInterval(webIntervalRef.current);
        webIntervalRef.current = null;
      }
    };
  }, [enabled]);

  return { location, error };
};
